import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Offcanvas from "react-bootstrap/Offcanvas";
import { toAbsoluteUrl } from "../../../../_metronic/helpers";
import { useUserAuth } from "../../../../context/AuthContext";
import { useBreakpoints } from "../../../../hooks/useBreakpoints";
import { usePortal } from "../../../../context/PortalContext";
import MenuItem from "./MenuItem";
import { getPartnerForCurrentHostname } from "../partnerConfig";

const TOOLS_LINKS = [
    { label: "Annuity quotes", to: "/annuityquotes" },
    { label: "Pension review", to: "/pensionreview" },
    { label: "Book an appointment", to: "/bookifacall" },
    { label: "Cost of delay", to: "/costofdelay" },
];

const NAV_H_MOBILE = 48;
const LOGO_H_MOBILE_MIN = 12;
const LOGO_H_MOBILE_MAX = 25;
const LOGO_W_MOBILE_MAX = 100;
const LOGO_H_DESKTOP_DEFAULT = 28;
const PENSE_STACK_H_DESKTOP = 24;
const VR_H_MOBILE = 26;
const VR_H_DESKTOP = 32;

const imgBlock = { display: "block", width: "auto" } as const;
const avatarSrc = toAbsoluteUrl("/media/avatars/blank.png");

const TOOLS_HIDDEN_URLS = ["/annuityquotesquick", "/fullquotepartial", "/factfind", "/pensionreview"];
const LOCKED_LOGGED_OUT_QUOTE_ROUTE = "/annuityquotesquickloggedout";

export default function Navbar() {
    const { logout, user, loading: authLoading } = useUserAuth();
    const { clientData, clearClientContext }: any = usePortal();
    const { isSm } = useBreakpoints();
    const { pathname } = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    // Locked down (no sign-in link, no menu, unclickable logo) only for the
    // high-fund flow - a repeat-enquiry visitor whose email already has an
    // account gets the normal nav so they can actually sign in from here.
    const lockNavigation =
        pathname === LOCKED_LOGGED_OUT_QUOTE_ROUTE &&
        sessionStorage.getItem("loggedOutQuickQuoteReason") !== "accountExists";

    const isPostSale = clientData?.annuity?.milestones?.some(
        (m: any) => m.milestoneId === "100197" && m.completed === true
    ) ?? false;

    const showTools = user && !authLoading && !TOOLS_HIDDEN_URLS.includes(pathname) && !isPostSale;

    const partner = useMemo(getPartnerForCurrentHostname, []);
    const hasPartner = !!partner;

    const penseLogo = toAbsoluteUrl("/media/logos/logopense.png");
    const partnerLogo = hasPartner ? toAbsoluteUrl(partner!.src) : "";

    const combinedHeaderStyle: React.CSSProperties = {
        minHeight: NAV_H_MOBILE,
        alignContent: "space-evenly",
        ...(partner?.navBgColor ? { backgroundColor: partner.navBgColor } : {}),
        ...(partner?.textColor ? { color: partner.textColor } : {}),
    };

    const vrMobileStyle: React.CSSProperties = {
        height: VR_H_MOBILE,
        ...(partner?.textColor ? { borderColor: partner.textColor } : {}),
    };
    const vrDesktopStyle: React.CSSProperties = {
        height: VR_H_DESKTOP,
        ...(partner?.textColor ? { borderColor: partner.textColor } : {}),
    };

    const handleLogout = async () => {
        await logout(clientData?.id);
        await clearClientContext();
        window.location.href = "/";
    };

    const accountMenuHeader = user && (
        <div className="d-flex align-items-center">
            <div className="symbol symbol-50px me-5">
                <img alt="" src={avatarSrc} />
            </div>
            <div className="d-flex flex-column overflow-hidden">
                <div className="fw-bolder d-flex align-items-center fs-5">
                    {user.displayName}
                    <span className="badge badge-light-success fw-bolder fs-8 px-2 py-1 ms-2">
                        Logged in
                    </span>
                </div>
                <span className="fw-bold text-muted fs-7">{user.email}</span>
            </div>
        </div>
    );

    const accountTrigger = (
        <div className="symbol symbol-35px symbol-md-40px">
            <img src={avatarSrc} alt="User avatar" />
        </div>
    );

    const accountLinks = [
        { label: "My dashboard", to: "/overview" },
        { label: "My details", to: "/my-details" },
        { label: "Sign out", onClick: handleLogout },
    ];

    return (
        <div
            id="kt_app_header"
            className="app-header app-container py-4"
            style={combinedHeaderStyle}
        >
            <nav className="navbar navbar-expand-md w-100 px-3 p-0">

                {/* Logo — always visible */}
                <div className="d-flex align-items-center">
                    {!hasPartner ? (
                        lockNavigation ? (
                            <span className="d-inline-flex align-items-center navbar-brand mb-0">
                                <img
                                    alt="Pense"
                                    src={penseLogo}
                                    className="h-30px h-lg-35px app-sidebar-logo-default theme-light-show"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </span>
                        ) : (
                        <Link to="/" className="d-inline-flex align-items-center navbar-brand">
                            <img
                                alt="Pense"
                                src={penseLogo}
                                className="h-30px h-lg-35px app-sidebar-logo-default theme-light-show"
                                loading="lazy"
                                decoding="async"
                            />
                        </Link>
                        )
                    ) : (
                        <div className="d-flex align-items-center">
                            {lockNavigation ? (
                                <span>
                                    <img
                                        alt={partner!.alt}
                                        src={partnerLogo}
                                        style={isSm ? {
                                            ...imgBlock,
                                            minHeight: LOGO_H_MOBILE_MIN,
                                            maxHeight: LOGO_H_MOBILE_MAX,
                                            maxWidth: LOGO_W_MOBILE_MAX,
                                        } : {
                                            ...imgBlock,
                                            height: partner?.caps?.desktop ?? LOGO_H_DESKTOP_DEFAULT,
                                        }}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </span>
                            ) : (
                                <Link to="/">
                                <img
                                    alt={partner!.alt}
                                    src={partnerLogo}
                                    style={isSm ? {
                                        ...imgBlock,
                                        minHeight: LOGO_H_MOBILE_MIN,
                                        maxHeight: LOGO_H_MOBILE_MAX,
                                        maxWidth: LOGO_W_MOBILE_MAX,
                                    } : {
                                        ...imgBlock,
                                        height: partner?.caps?.desktop ?? LOGO_H_DESKTOP_DEFAULT,
                                    }}
                                    loading="lazy"
                                    decoding="async"
                                />
                                </Link>
                            )}
                            <div className="vr mx-3" style={isSm ? vrMobileStyle : vrDesktopStyle} />
                            <div className="d-flex flex-column justify-content-center lh-1">
                                <span className={`small text-nowrap${partner?.textColor ? "" : " text-muted"}`}>
                                    working together with
                                </span>
                                {lockNavigation ? (
                                    <span>
                                        <img
                                            alt="Pense"
                                            src={penseLogo}
                                            style={isSm ? {
                                                ...imgBlock,
                                                minHeight: LOGO_H_MOBILE_MIN,
                                                maxHeight: LOGO_H_MOBILE_MAX,
                                                maxWidth: LOGO_W_MOBILE_MAX,
                                            } : {
                                                ...imgBlock,
                                                maxHeight: PENSE_STACK_H_DESKTOP,
                                            }}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </span>
                                ) : (
                                    <Link to="/">
                                    <img
                                        alt="Pense"
                                        src={penseLogo}
                                        style={isSm ? {
                                            ...imgBlock,
                                            minHeight: LOGO_H_MOBILE_MIN,
                                            maxHeight: LOGO_H_MOBILE_MAX,
                                            maxWidth: LOGO_W_MOBILE_MAX,
                                        } : {
                                            ...imgBlock,
                                            maxHeight: PENSE_STACK_H_DESKTOP,
                                        }}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    </Link>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {isSm ? (
                    lockNavigation ? null : user ? (
                        <button
                            className="navbar-toggler border-0 ms-auto"
                            type="button"
                            aria-label="Toggle navigation"
                            onClick={() => setDrawerOpen(true)}
                        >
                            <span
                                className="navbar-toggler-icon"
                                style={partner?.textColor ? { filter: "invert(1)" } : undefined}
                            />
                        </button>
                    ) : (
                        <Link
                            to="/auth/login"
                            className={`btn btn-sm ms-auto${partner?.textColor ? " btn-signin-light border border-1 border-secondary" : " btn-signin"}`}
                        >
                            Sign in
                        </Link>
                    )
                ) : (
                    <div className="ms-auto d-flex align-items-center gap-3">
                        {lockNavigation ? null : user ? (
                            <>
                                {showTools && <MenuItem label="Tools" links={TOOLS_LINKS} triggerClassName={`nav-item-toggle${partner?.textColor === "white" ? " nav-item-toggle-light" : ""}`} />}
                                <MenuItem
                                    label="My account"
                                    triggerContent={accountTrigger}
                                    triggerClassName="nav-item-toggle nav-item-toggle-icon cursor-pointer"
                                    menuHeader={accountMenuHeader || undefined}
                                    links={accountLinks}
                                />
                            </>
                        ) : (
                            <Link
                                to="/auth/login"
                                className={`btn btn-sm${partner?.textColor ? " btn-signin-light border border-1 border-secondary" : " btn-signin"}`}
                            >
                                Sign in
                            </Link>
                        )}
                    </div>
                )}
            </nav>

            {/* Mobile drawer — only reachable when logged in */}
            {isSm && user && !lockNavigation && <Offcanvas
                show={drawerOpen}
                onHide={() => setDrawerOpen(false)}
                placement="end"
                responsive="md"
            >
                <Offcanvas.Header closeButton>
                    <Link to="/" onClick={() => setDrawerOpen(false)}>
                        <img
                            alt="Pense"
                            src={penseLogo}
                            style={{ height: 28, width: "auto" }}
                            loading="lazy"
                            decoding="async"
                        />
                    </Link>
                </Offcanvas.Header>
                <Offcanvas.Body className="pt-0">
                    <div className="d-flex flex-column gap-10">
                        <div className="d-flex align-items-center p-3 rounded-2 bg-gray-100">
                            <div className="symbol symbol-50px me-5">
                                <img alt="" src={avatarSrc} />
                            </div>
                            <div className="d-flex flex-column overflow-hidden">
                                <div className="fw-bolder d-flex align-items-center fs-5">
                                    {user.displayName}
                                    <span className="badge badge-light-success fw-bolder fs-8 px-2 py-1 ms-2">
                                        Logged in
                                    </span>
                                </div>
                                <span className="fw-bold text-muted fs-7">{user.email}</span>
                            </div>
                        </div>
                        <div className="d-flex flex-column gap-5">
                            <div className="accordion accordion-flush">
                                {showTools && (
                                    <MenuItem
                                        label="Tools"
                                        links={TOOLS_LINKS.map((l) => ({ ...l, onClick: () => setDrawerOpen(false) }))}
                                        triggerClassName={`nav-item-toggle${partner?.textColor === "white" ? " nav-item-toggle-light" : ""}`}
                                    />
                                )}
                            </div>
                            <Link
                                to="/overview"
                                className="nav-item-toggle pt-2"
                                onClick={() => setDrawerOpen(false)}
                            >
                                My dashboard
                            </Link>
                                                        <Link
                                to="/my-details"
                                className="nav-item-toggle pt-2"
                                onClick={() => setDrawerOpen(false)}
                            >
                                My details
                            </Link>
                            <div className="border-top mt-20"> 
                                <button
                                className="nav-item-toggle mt-5"
                                onClick={handleLogout}
                            >
                                Sign out
                            </button>
                            </div>

                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>}
        </div>
    );
}
